import * as _ from "lodash-es";
import UserInfo from "../types/UserInfo";
import {
	RelationApplicationsRow,
	UserRelationsRow,
} from "@/lib/shared/UserTypes";
import {USER_ROLE_MAP, UserRole} from "@/shared/UserRole";

export const STAFF_ROLES = [
	USER_ROLE_MAP.OFFICE_STAFF,
	USER_ROLE_MAP.ADMIN,
	USER_ROLE_MAP.INSTRUCTOR,
	USER_ROLE_MAP.DIRECTOR,
];

export const STUDENT_ROLES = [
	USER_ROLE_MAP.STUDENT,
	USER_ROLE_MAP.PLATFORM_STUDENT,
	USER_ROLE_MAP.LIMITED,
];

/**
 * Determines whether or not a user has any staff role.
 * @param userInfo the user to evaluate
 * @returns True if the given user is staff, otherwise returns false.
 */
const isStaff = (userInfo: UserInfo) => {
	return hasAnyRole(userInfo, STAFF_ROLES);
};

/**
 * Determines whether a user has any student role.
 */
const isStudent = (userInfo: UserInfo) => {
	return hasAnyRole(userInfo, STUDENT_ROLES);
};

/**
 * Determines whether a user is a parent.
 */
const isParent = (userInfo: UserInfo) => {
	return hasRole(userInfo, USER_ROLE_MAP.PARENT);
};

/**
 * Determines whether a user is an admin.
 */
const isAdmin = (userInfo: UserInfo) => {
	return hasRole(userInfo, USER_ROLE_MAP.ADMIN);
};

/**
 * Determines whether a user is an unknown user type.
 *
 * We define an unknown user as one who is logged in, but we don't know if they're a student or parent.
 * Currently we check this by checking if the user has no roles. This applies to online-only users.
 */
const isUnknownUserType = (userInfo: UserInfo | null) => {
	return userInfo && userInfo.roles.length === 0;
};

/**
 * Determines whether or not a user has at least one (1) of an array of roles.
 * @param user the user to evaluate
 * @param roles an array of roles, of which the user must have one.
 * @returns True if the given user has any one of the given roles, otherwise returns false.
 */
const hasAnyRole = (user: UserInfo | null = null, roles: UserRole[]) => {
	return roleArrayHasAnyRole(user?.roles ?? [], roles);
};

/**
 * Determines whether or not two UserRole arrays have at least one role in common.
 * @param userRoles the roles extracted from the "user".
 *                  Technically this can be any arbitrary role array.  I just
 *                  didn't want to name the arrays like...roles0 and roles1
 * @param roles an array of roles to compare userRoles against.
 * @returns True if userRoles and roles have any non-trivial intersection.  Otherwise returns false.
 */
const roleArrayHasAnyRole = (userRoles: UserRole[], roles: UserRole[]) => {
	if (_.intersection(userRoles, roles).length) {
		return true;
	}
	return false;
};

/**
 * Determines whether or not a user has the given role.
 * @param user the user to evaluate
 * @param role a role to check for
 * @returns True if the given user has the given role, otherwise returns false.
 */
const hasRole = (user: UserInfo | null = null, role: UserRole) => {
	return hasAnyRole(user, [role]);
};

/**
 * Determines whether or not a user has num roles out of an array of roles.  If
 * num is not specified, this function will return true only if the user has all the
 * roles in the array.
 *
 * If num is greater than the length of the roles array, this
 * function will return true if the user has all of the roles specified.
 *
 * @param user the user to evaluate
 * @param roles the array of roles of which the user must have either num, or all.
 * @param num (optional) the number of roles the user must match in order for the function to return true.
 * @returns true if the given user has at least min(num, roles.length) of the roles specified.  Otherwise returns false.
 */
const hasManyRoles = (
	user: UserInfo,
	roles: UserRole[],
	num = Number.POSITIVE_INFINITY
) => {
	if (num !== Number.POSITIVE_INFINITY && num > roles.length) {
		return false;
	}

	if (_.intersection(user.roles, roles).length >= Math.min(roles.length, num)) {
		return true;
	}

	return false;
};

/**
 * Returns the display name for a user relation, i.e. their real name or username on a given application.
 */
const getRelationDisplayName = ({
	firstName,
	lastName,
	username,
	displayName,
	applications,
}: UserRelationsRow) => {
	if (firstName) {
		return `${firstName} ${lastName}`;
	}

	const primaryName =
		username ??
		_.find(applications, "applicationUsername")?.applicationUsername ??
		"";

	return `${primaryName} (${displayName})`;
};

/**
 * Determines whether or not a given relation row is an initialized student Academy account.
 */
const isStudentAccountInitialized = (student: UserRelationsRow) => {
	return student.applications.some((application) => {
		return (
			application.applicationCode === "academy" && application.isInitialized
		);
	});
};

/**
 * Returns the user relation row for a student's Academy account if it exists.
 */
const getStudentAcademyAccount: (
	student: UserRelationsRow
) => RelationApplicationsRow | null = (student: UserRelationsRow) => {
	for (const application of student.applications) {
		if (application.applicationCode === "academy") {
			return application;
		}
	}

	return null;
};

export {
	isStaff,
	isStudent,
	isParent,
	isAdmin,
	isUnknownUserType,
	hasRole,
	hasAnyRole,
	hasManyRoles,
	roleArrayHasAnyRole,
	getRelationDisplayName,
	isStudentAccountInitialized,
	getStudentAcademyAccount,
};
